import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { LoginFormData } from '../../types/auth';
import './LoginForm.css';

// Validation schema using Yup
const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  password: Yup.string()
    .required('Password is required')
});

// Initial form values
const initialValues: LoginFormData = {
  email: '',
  password: ''
};

interface LoginFormProps {
  onSuccess?: () => void;
  onSwitchToRegister?: () => void;
  onForgotPassword?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ 
  onSuccess, 
  onSwitchToRegister,
  onForgotPassword 
}) => {
  const { signIn, isLoading, error, clearError } = useAuth();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async (
    values: LoginFormData,
    { setSubmitting, setFieldError }: any
  ) => {
    try {
      clearError();
      setSuccessMessage(null);

      const result = await signIn(values);
      
      if (result.success) {
        setSuccessMessage('Login successful! Redirecting...');
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 1000); // Give user time to see success message
        }
      } else {
        // Handle specific field errors if needed
        if (result.message?.includes('email')) {
          setFieldError('email', result.message);
        } else if (result.message?.includes('password')) {
          setFieldError('password', result.message);
        }
      }
    } catch (err) {
      console.error('Login error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-form-container">
      <div className="login-form-card">
        <div className="form-header">
          <h2>Welcome Back</h2>
          <p>Sign in to continue your speech development journey</p>
        </div>

        {successMessage && (
          <div className="success-message" role="alert">
            <div className="success-icon">✓</div>
            <div className="success-text">{successMessage}</div>
          </div>
        )}

        {error && (
          <div className="error-message" role="alert">
            <div className="error-icon">⚠</div>
            <div className="error-text">{error}</div>
          </div>
        )}

        <Formik
          initialValues={initialValues}
          validationSchema={loginSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, touched, errors }) => (
            <Form className="login-form">
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email Address *
                </label>
                <Field
                  type="email"
                  id="email"
                  name="email"
                  className={`form-input ${touched.email && errors.email ? 'error' : ''}`}
                  placeholder="Enter your email address"
                  disabled={isLoading || isSubmitting}
                />
                <ErrorMessage name="email" component="div" className="field-error" />
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password *
                </label>
                <Field
                  type="password"
                  id="password"
                  name="password"
                  className={`form-input ${touched.password && errors.password ? 'error' : ''}`}
                  placeholder="Enter your password"
                  disabled={isLoading || isSubmitting}
                />
                <ErrorMessage name="password" component="div" className="field-error" />
              </div>

              <button
                type="submit"
                className={`submit-button ${isLoading || isSubmitting ? 'loading' : ''}`}
                disabled={isLoading || isSubmitting}
              >
                {isLoading || isSubmitting ? (
                  <>
                    <span className="loading-spinner"></span>
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </Form>
          )}
        </Formik>

        <div className="form-links">
          <button
            type="button"
            className="link-button forgot-password-link"
            onClick={onForgotPassword}
            disabled={isLoading}
          >
            Forgot Password?
          </button>
        </div>

        <div className="form-footer">
          <p>
            Don't have an account?{' '}
            <button
              type="button"
              className="link-button"
              onClick={onSwitchToRegister}
              disabled={isLoading}
            >
              Sign Up
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;